import { ChevronDownOutlineIcon } from '@applyboard/ui-icons';
import * as RadixAccordion from '@radix-ui/react-accordion';
import React from 'react';
import { Text } from '../Text';
import { FullTheme, IconType, Responsive, SpacingToken, useFullTheme } from '~/design';
import { IconWrapper } from '~/internal';
import { findChildrenOfType, useResponsive } from '~/utils';
import { keyframes } from '@emotion/react';

export interface AccordionItemProps {
  /** If true, the accordion item is open by default. */
  autoOpen?: boolean;

  /** The content of the accordion item. */
  children: React.ReactNode;

  /** If true, the accordion item is disabled. */
  disabled?: boolean;

  /** If true, the chevron will be hidden. */
  hideChevron?: boolean;

  /** The icon of the accordion item. */
  icon?: IconType;

  /** The ID of the accordion item. This does _not_ set the HTML 'id' attribute. */
  id: string;

  /** The title of the accordion item. */
  title: React.ReactNode;
}

export interface AccordionProps {
  /** If true, multiple items can be open at once. */
  allowMultipleOpen?: boolean;

  /** The content of the accordion. */
  children: React.ReactNode;

  /** If true, all of the accordion's items are disabled. */
  disabled?: boolean;

  /** The emphasis of the Accordion. `outlined` will render a border, `plain` will not. */
  emphasis?: 'outlined' | 'plain';

  /** The gap between accordion items. If set, all accordion items will have rounded borders. */
  gap?: SpacingToken | Responsive<SpacingToken>;

  /** * Manually control if an accordion item is open by providing its id.
   * If a value is provided, overrides the default behaviour of the accordion. */
  value?: string | string[];

  /** A function that gets called when any item is opened or closed. Useful for event tracking. */
  onChange?: (value: string | string[]) => void;

  px?: 1;
}

/**
 * An accordion is a vertically stacked list of headers that can be clicked to reveal or hide content associated with them.
 * - It is one of many ways you can expose content to users in a progressive manner.
 * - Allowing people to have control over the content by expanding it or deferring it for later lets them decide what to read and what to ignore.
 * - Accordions are ideal for breaking down long-form or complex content into digestible chunks thus reducing the cognitive load.
 * - It’s also ideal for mobile sites since it reduces how much a user has to scroll.
 *
 * ## Accessibility ♿
 * - Accessibility features:
 *   - Uses correct aria attributes according to https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 *   - Visible focus state for keyboard users
 *   - Space or enter activates accordion
 */
export function Accordion({
  allowMultipleOpen = true,
  children,
  disabled = false,
  emphasis = 'outlined',
  gap: _gap,
  onChange,
  value,
  px,
}: AccordionProps) {
  const { borderRadiuses, colors, spacing } = useFullTheme();
  const border = `1px solid ${colors.borderRegular}`;
  const borderTransparent = `1px solid transparent`;
  const outlineColor = colors.actionPrimary;
  const { accordionValue, type, numItems, updateValue } = useAccordionValue({
    allowMultipleOpen,
    children,
    controlledValue: value,
  });
  const responsive = useResponsive();
  const gap = !_gap ? { xs: 0 as SpacingToken } : typeof _gap === 'object' ? _gap : { xs: _gap };

  const filteredChildren = findChildrenOfType(children, [Accordion.Item]);

  return (
    // @ts-expect-error radix-ui's FIXME: Accordion is very picky about the types it receives. All
    // of the props we're passing are valid but some are only valid for type='single' while some
    // are only valid for type='multiple'.
    <RadixAccordion.Root
      css={[
        {
          display: 'flex',
          flexDirection: 'column',
        },
        responsive.css({
          gap: responsive.map(gap, val => spacing[val]),
        }),
      ]}
      collapsible={type === 'multiple' ? undefined : true}
      disabled={disabled}
      onValueChange={(newValue: string | string[]) => {
        onChange?.(newValue);
        updateValue(newValue);
      }}
      type={type}
      value={accordionValue}
    >
      {filteredChildren.map((child, index) => {
        const Icon = child.props.icon;
        const isDisabled = disabled || child.props.disabled;

        const borderRadiusStyles = [
          responsive.css({
            borderTopLeftRadius: responsive.map(gap, val =>
              val || index === 0 ? borderRadiuses.control : 0,
            ),
            borderTopRightRadius: responsive.map(gap, val =>
              val || index === 0 ? borderRadiuses.control : 0,
            ),
            borderBottomLeftRadius: responsive.map(gap, val =>
              val || index === numItems - 1 ? borderRadiuses.control : 0,
            ),
            borderBottomRightRadius: responsive.map(gap, val =>
              val || index === numItems - 1 ? borderRadiuses.control : 0,
            ),
          }),
        ];

        return (
          <RadixAccordion.Item
            css={[
              {
                backgroundColor: colors.bgRegular,
                '&:focus': { outline: 'none' },
              },
              emphasis === 'plain' && {
                borderTop: index === 0 ? borderTransparent : border,
                borderLeft: borderTransparent,
                borderRight: borderTransparent,
              },
              emphasis === 'outlined' && {
                borderLeft: border,
                borderRight: border,
                borderTop: border,
              },
              ...(emphasis === 'outlined' ? borderRadiusStyles : []),
              responsive.css({
                borderBottom: responsive.map(gap ?? { xs: 0 }, val => {
                  return val || (index === numItems - 1 && emphasis === 'outlined')
                    ? border
                    : borderTransparent;
                }),
              }),
            ]}
            data-element="accordionItem"
            disabled={child.props.disabled}
            key={child.props.id}
            value={child.props.id}
          >
            <RadixAccordion.Header
              asChild
              css={[
                {
                  backgroundColor: 'transparent',
                  border: 0,
                  margin: 0,
                  padding: `${spacing[2]} ${px === 1 ? spacing[1] : spacing[6]}`,
                  textAlign: 'left',
                  width: '100%',
                },
                !isDisabled && {
                  '&:hover': {
                    backgroundColor: colors.bgRegularHover,
                  },
                  '&:active': {
                    backgroundColor: colors.bgRegularPressed,
                  },
                },
                ...borderRadiusStyles,
                {
                  '&[data-state=open]': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                },
              ]}
            >
              <RadixAccordion.Trigger
                css={[
                  {
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    '&:focus': {
                      boxShadow: `inset ${outlineColor} 0px 0px 0px 1px, ${outlineColor} 0px 0px 0px 1px`,
                      outline: 'none',
                    },
                  },
                  isDisabled && {
                    cursor: 'not-allowed',
                  },
                ]}
              >
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: spacing[2],
                  }}
                >
                  <div
                    css={{
                      alignItems: 'center',
                      display: 'flex',
                      flexGrow: 1,
                      gap: spacing[4],
                    }}
                  >
                    {Icon && (
                      <IconWrapper
                        disabled={isDisabled}
                        intent="secondary"
                        selector="button"
                        contrast="high"
                      >
                        <Icon />
                      </IconWrapper>
                    )}
                    <div css={{ flexGrow: 1 }}>
                      <Text contrast={isDisabled ? 'low' : undefined} variant="titleS">
                        {child.props.title}
                      </Text>
                    </div>
                  </div>

                  {!child.props.hideChevron && (
                    <span
                      css={{
                        lineHeight: 0,
                        transition: 'transform 300ms',
                        '[data-element=accordionItem] [data-state=open] &': {
                          transform: 'rotate(180deg)',
                        },
                      }}
                    >
                      <IconWrapper
                        disabled={isDisabled}
                        intent="secondary"
                        selector="button"
                        contrast="high"
                      >
                        <ChevronDownOutlineIcon />
                      </IconWrapper>
                    </span>
                  )}
                </div>
              </RadixAccordion.Trigger>
            </RadixAccordion.Header>
            <RadixAccordion.Content
              css={{
                borderTop: border,
                overflow: 'hidden',
                paddingInline: px === 1 ? spacing[1] : spacing[6],
                '&[data-state="open"]': {
                  '@media (prefers-reduced-motion: no-preference)': {
                    animationDirection: 'normal',
                    animationDuration: '0.3s',
                    animationName: slideDownAnimation(spacing),
                    animationTimingFunction: 'ease-out',
                  },
                  paddingBlock: spacing[6],
                },
                '&[data-state="closed"]': {
                  '@media (prefers-reduced-motion: no-preference)': {
                    animationDirection: 'normal',
                    animationDuration: '0.3s',
                    animationName: slideUpAnimation(spacing),
                    animationTimingFunction: 'ease-out',
                  },
                  paddingBlock: spacing[6],
                },
              }}
            >
              <Text variant="bodyM">{child.props.children}</Text>
            </RadixAccordion.Content>
          </RadixAccordion.Item>
        );
      })}
    </RadixAccordion.Root>
  );
}

Accordion.Item = function AccordionItem(props: AccordionItemProps) {
  return <>{props.children}</>;
};

function useAccordionValue({
  allowMultipleOpen,
  children,
  controlledValue,
}: {
  allowMultipleOpen: boolean;
  children: AccordionProps['children'];
  controlledValue?: string | string[];
}) {
  const type: 'multiple' | 'single' =
    Array.isArray(controlledValue) || allowMultipleOpen ? 'multiple' : 'single';
  const accordionItemChildren = React.Children.toArray(
    children,
  ) as unknown as React.ReactElement<AccordionItemProps>[];

  const defaultValue = allowMultipleOpen
    ? accordionItemChildren.filter(child => child.props.autoOpen).map(child => child.props.id)
    : accordionItemChildren.find(item => item.props.autoOpen)?.props.id;

  const [accordionValue, setAccordionValue] = React.useState(defaultValue);

  const updateValue = (newValue: string | string[] | undefined) => {
    if (!controlledValue) {
      setAccordionValue(newValue);
    }
  };

  return {
    accordionValue: controlledValue ? controlledValue : accordionValue,
    numItems: accordionItemChildren.length,
    type,
    updateValue,
  };
}

const slideDownAnimation = (spacing: FullTheme['spacing']) => keyframes`
	from {
		height: 0;
    padding-block: 0;
	}
	to {
		height: var(--radix-accordion-content-height);
    padding-block: ${spacing[6]};
	}
`;
const slideUpAnimation = (spacing: FullTheme['spacing']) => keyframes`
	from {
		height: var(--radix-accordion-content-height);
    padding-block: ${spacing[6]};
	}
	to {
		height: 0;
    padding-block: 0;
	}
`;
